import { Assert } from "../Utils/Assert";
import { IEventSubscriber } from "./IEventSubscriber";

export class Dispatcher<TEventPayload> {
    private _isDispatching = false;

    private _registrations: Map<IEventSubscriber, (eventData: TEventPayload) => void> = new Map<
        IEventSubscriber,
        (eventData: TEventPayload) => void
    >();

    private _pendingHandlers: Array<IEventSubscriber> = [];

    private _finishedHandlers: Array<IEventSubscriber> = [];

    private _lastEventPayload: TEventPayload;

    public register(subscriber: IEventSubscriber, onDispatch: (eventData: TEventPayload) => void): void {
        Assert(!this._registrations.has(subscriber), "Dispatcher.register: This subscriber is already registered.");
        this._registrations.set(subscriber, onDispatch);
    }

    public unregister(subscriber: IEventSubscriber): void {
        Assert(this._registrations.has(subscriber), "Dispatcher.unregister: This subscriber is not registered.");
        this._registrations.delete(subscriber);
    }

    public dispatch(eventData: TEventPayload): void {
        Assert(!this._isDispatching, "Dispatcher.dispatch: Already dispatching an event.");

        this.startDispatching(eventData);

        this._registrations.forEach((callback, subscriber) => this.invokeCallback(subscriber, callback));

        this.stopDispatching();
    }

    public waitFor(...subscribers: IEventSubscriber[]): void {
        Assert(this._isDispatching, "Dispatcher.waitFor: Must be invoked while dispatching.");

        for (let ii = 0; ii < subscribers.length; ii++) {
            const subscriber = subscribers[ii];

            if (this.isPending(subscriber)) {
                Assert(
                    this.isFinished(subscriber),
                    "Dispatcher.waitFor: Circular dependency detected while waiting for {0}.",
                    subscriber
                );
                continue;
            }

            Assert(
                this._registrations.has(subscriber),
                "Dispatcher.waitFor: Trying to wait for a not registered callback"
            );

            this.invokeCallback(subscriber, this._registrations.get(subscriber));
        }
    }

    public isDispatching(): boolean {
        return this._isDispatching;
    }

    private invokeCallback(subscriber: IEventSubscriber, subscriberCallback: (eventData: TEventPayload) => void): void {
        if (this.isPending(subscriber)) {
            return;
        }

        this._pendingHandlers.push(subscriber);
        subscriberCallback(this._lastEventPayload);
        this._finishedHandlers.push(subscriber);
    }

    private startDispatching(eventData: TEventPayload): void {
        this._pendingHandlers = [];
        this._finishedHandlers = [];
        this._isDispatching = true;
        this._lastEventPayload = eventData;
    }

    private stopDispatching(): void {
        Assert(
            this._pendingHandlers.length === this._finishedHandlers.length,
            "Dispatcher.stopDispatching: Not all handlers finished dispatching."
        );
        this._isDispatching = false;
        delete this._lastEventPayload;
    }

    private isPending(subscriber: IEventSubscriber): boolean {
        return this._pendingHandlers.some((pendingHandler) => pendingHandler === subscriber);
    }

    private isFinished(subscriber: IEventSubscriber): boolean {
        return this._finishedHandlers.some((pendingHandler) => pendingHandler === subscriber);
    }
}
